<template>
  <div class="temporal-tab">
    <div class="chart-layout">
      <div class="chart-settings">
        <div class="settings-title">Настройки графика</div>
        <div class="settings-section">
          <div class="settings-label">Режим отображения</div>
          <div class="metrics-list">
            <button
              v-for="mode in CHART_MODES"
              :key="mode.id"
              type="button"
              class="metric-chip mode-chip"
              :class="{ selected: chartMode === mode.id }"
              @click="chartMode = mode.id"
            >
              {{ mode.label }}
            </button>
          </div>
          <div class="settings-hint">{{ MODE_HINTS[chartMode] }}</div>
        </div>
        <div class="settings-section" v-for="group in metricGroups" :key="group.label">
          <div class="settings-label">{{ group.label }}</div>
          <div class="metrics-list">
            <button
              v-for="metric in group.metrics"
              :key="metric.field"
              type="button"
              class="metric-chip"
              :class="{ selected: isSelected(metric) }"
              :title="isSelected(metric) ? 'Убрать метрику (Shift+клик — только эта)' : metric.label"
              @click="toggleMetric(metric, $event)"
            >
              <span class="metric-dot" :style="{ background: metric.color }" />
              {{ metric.label }}
            </button>
          </div>
        </div>
        <div class="settings-section">
          <div v-if="selectedFields.length > 1" class="settings-hint">{{ multiHint }}</div>
          <div class="settings-hint">Shift+клик — оставить только эту метрику</div>
        </div>
      </div>
      <div class="chart-block">
        <div class="chart-title">Показатели по корзинам</div>
        <VChart
          v-if="rows.length"
          ref="chartRef"
          class="chart"
          :option="chartOption"
          autoresize
          @finished="activateZoomSelect"
        />
        <div v-else-if="isLoading" class="chart-empty">Загрузка…</div>
        <div v-else class="chart-empty">Нет данных по выбранным фильтрам</div>
      </div>
    </div>
    <div class="table-scroll">
      <table class="stat-table" v-if="rows.length">
        <thead>
          <tr>
            <th>Корзина</th>
            <th>Рейсов</th>
            <th>Открыт</th>
            <th>Закрыт</th>
            <th>Новых</th>
            <th>Наш ТС</th>
            <th>Бот</th>
            <th>Отменен</th>
            <th>Срыв</th>
            <th>Отказ</th>
            <th>Удалено</th>
            <th>Устарело</th>
            <th>Без карты</th>
            <th>Комиссия</th>
            <th>Чистая</th>
            <th>Ср. комиссия</th>
            <th>Ср. цена клиента</th>
            <th>Штрафы за срывы</th>
            <th>Маржа</th>
            <th>Рентаб.</th>
            <th>Откл. базы</th>
            <th>Часы до погрузки</th>
            <th>Опоздания</th>
            <th>≥24 ч</th>
            <th>12–24 ч</th>
            <th>&lt;12 ч</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in [...rows].reverse()" :key="`${row.bin_label}-${idx}`">
            <td class="id-cell">{{ row.bin_label }}</td>
            <td>{{ fmtNum(row.trips_total) }}</td>
            <td>{{ fmtNum(row.opened) }}</td>
            <td>{{ fmtNum(row.closed) }}</td>
            <td>{{ fmtNum(row.new_trips) }}</td>
            <td>{{ fmtNum(row.own_fleet) }}</td>
            <td>{{ fmtNum(row.bot_wins) }}</td>
            <td>{{ fmtNum(row.canceled) }}</td>
            <td :class="{ 'cell-warn': Number(row.breakdown) > 0 }">{{ fmtNum(row.breakdown) }}</td>
            <td>{{ fmtNum(row.refusal) }}</td>
            <td>{{ fmtNum(row.deleted) }}</td>
            <td>{{ fmtNum(row.outdated) }}</td>
            <td>{{ fmtNum(row.no_card) }}</td>
            <td>{{ fmtMoney(row.total_commission) }}</td>
            <td>{{ fmtMoney(row.net_commission_rub) }}</td>
            <td>{{ fmtMoney(row.avg_commission) }}</td>
            <td>{{ fmtMoney(row.avg_customer_gross) }}</td>
            <td :class="{ 'cell-warn': Number(row.breakdown_loss_rub) > 0 }">{{ fmtMoney(row.breakdown_loss_rub) }}</td>
            <td>{{ fmtPct(row.avg_margin_pct) }}</td>
            <td>{{ fmtPct(row.avg_profitability_pct) }}</td>
            <td>{{ fmtPct(row.avg_base_deviation_pct) }}</td>
            <td>{{ fmtNum(row.avg_hours_to_load, 1) }}</td>
            <td>{{ fmtNum(row.expired_total) }}</td>
            <td>{{ fmtNum(row.closed_early) }}</td>
            <td>{{ fmtNum(row.closed_lastday) }}</td>
            <td>{{ fmtNum(row.closed_late) }}</td>
          </tr>
        </tbody>
      </table>
      <my-loader v-else-if="isLoading" />
      <div v-else class="empty-table">Нет рейсов по заданным фильтрам</div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import VChart from '@/components/charts/echarts';
import { fmtMoney, fmtNum, fmtPct } from '../format';

// Метрики корзин: мультивыбор (как чипы метрик у вкладки «Логисты»)
const METRICS = [
  { field: 'trips_total', label: 'Рейсов', formatter: fmtNum, color: '#007bff', counter: true },
  { field: 'opened', label: 'Открыто', formatter: fmtNum, color: '#667085', counter: true },
  { field: 'closed', label: 'Закрыто', formatter: fmtNum, color: '#12b76a', counter: true },
  { field: 'new_trips', label: 'Новых', formatter: fmtNum, color: '#7a5af8', counter: true },
  { field: 'own_fleet', label: 'Наш ТС', formatter: fmtNum, color: '#0e9384', counter: true },
  { field: 'bot_wins', label: 'Бот', formatter: fmtNum, color: '#53b1fd', counter: true },
  { field: 'deleted', label: 'Удалено', formatter: fmtNum, color: '#98a2b3', counter: true },
  { field: 'outdated', label: 'Устарело', formatter: fmtNum, color: '#f2c94c', counter: true },
  { field: 'no_card', label: 'Без карточки', formatter: fmtNum, color: '#ff9f1c', counter: true },
  { field: 'total_commission', label: 'Комиссия', formatter: fmtMoney, color: '#3b82f6' },
  { field: 'net_commission_rub', label: 'Чистая комиссия', formatter: fmtMoney, color: '#1570ef' },
  { field: 'avg_commission', label: 'Ср. комиссия', formatter: fmtMoney, color: '#667085' },
  { field: 'avg_customer_gross', label: 'Ср. цена клиента', formatter: fmtMoney, color: '#15b79e' },
  { field: 'breakdown_loss_rub', label: 'Штрафы за срывы', formatter: fmtMoney, color: '#d92d20' },
  { field: 'avg_margin_pct', label: 'Маржа', formatter: fmtPct, color: '#12b76a' },
  { field: 'avg_profitability_pct', label: 'Рентабельность', formatter: fmtPct, color: '#7a5af8' },
  { field: 'avg_base_deviation_pct', label: 'Отклонение от базы', formatter: fmtPct, color: '#f79009' },
  { field: 'avg_hours_to_load', label: 'Часы до погрузки', formatter: (v) => fmtNum(v, 1), color: '#0ba5ec' },
  { field: 'breakdown', label: 'Срывы', formatter: fmtNum, color: '#f04438', counter: true },
  { field: 'canceled', label: 'Отменено', formatter: fmtNum, color: '#f79009', counter: true },
  { field: 'refusal', label: 'Отказы', formatter: fmtNum, color: '#f97066', counter: true },
  { field: 'expired_total', label: 'Опоздания', formatter: fmtNum, color: '#c01048', counter: true },
  { field: 'closed_early', label: '≥24 ч', formatter: fmtNum, color: '#039855', counter: true },
  { field: 'closed_lastday', label: '12–24 ч', formatter: fmtNum, color: '#dc6803', counter: true },
  { field: 'closed_late', label: '<12 ч', formatter: fmtNum, color: '#d92d20', counter: true },
];

// Группы как у вкладки «Логисты» общей статистики
const METRIC_GROUPS = [
  { label: 'Объёмы', fields: ['trips_total', 'opened', 'closed', 'new_trips', 'own_fleet', 'bot_wins', 'deleted', 'outdated', 'no_card'] },
  { label: 'Деньги', fields: ['total_commission', 'net_commission_rub', 'avg_commission', 'avg_customer_gross', 'breakdown_loss_rub'] },
  { label: 'Маржинальность и цены', fields: ['avg_margin_pct', 'avg_profitability_pct', 'avg_base_deviation_pct'] },
  { label: 'Сроки', fields: ['avg_hours_to_load'] },
  { label: 'Надёжность', fields: ['breakdown', 'canceled', 'refusal', 'expired_total', 'closed_early', 'closed_lastday', 'closed_late'] },
];

const CHART_MODES = [
  { id: 'grouped', label: 'Группировка' },
  { id: 'stacked', label: 'Стек' },
  { id: 'percent', label: 'Стек %' },
  { id: 'line', label: 'Линия' },
  { id: 'cumulative', label: 'Накопительно' },
];

const MODE_HINTS = {
  grouped: 'Каждая метрика — своя серия баров в корзине',
  stacked: 'Бары метрик складываются друг на друга в корзине — выбирайте метрики одной размерности',
  percent: 'Доля каждой метрики в сумме выбранных метрик корзины, % (стек до 100%)',
  line: 'Сглаженные линии значений метрик по корзинам',
  cumulative: 'Нарастающий итог метрики по корзинам (линии)',
};

const MULTI_HINTS = {
  grouped: 'Несколько метрик — сгруппированные бары',
  stacked: 'Несколько метрик — один стек в каждой корзине',
  percent: 'Несколько метрик — доли внутри одной корзины (сумма = 100%)',
  line: 'Несколько метрик — своя сглаженная линия для каждой',
  cumulative: 'Несколько метрик — линия нарастающего итога для каждой',
};

const DEFAULT_FIELDS = ['trips_total'];
const DEFAULT_MODE = 'grouped';
const METRICS_STORAGE_KEY = 'temporalTripsMetrics';
const MODE_STORAGE_KEY = 'temporalTripsChartMode';

function loadFields() {
  try {
    const raw = window.localStorage.getItem(METRICS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) {
      const valid = parsed.filter((f) => METRICS.some((m) => m.field === f));
      if (valid.length) return valid;
    }
  } catch (e) {
    /* localStorage может быть недоступен — молча игнорируем */
  }
  return [...DEFAULT_FIELDS];
}

function loadMode() {
  try {
    const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
    return CHART_MODES.some((m) => m.id === raw) ? raw : DEFAULT_MODE;
  } catch (e) {
    return DEFAULT_MODE;
  }
}

// Нарастающий итог по корзинам (rows идут по возрастанию корзины)
function cumulativeSeries(values) {
  let acc = 0;
  return values.map((v) => {
    acc += Number.isFinite(v) ? v : 0;
    return acc;
  });
}

export default {
  name: 'TemporalTripsTab',
  components: { VChart },
  props: {
    rows: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
  },
  setup(props) {
    const selectedFields = ref(loadFields());
    const chartMode = ref(loadMode());
    const chartRef = ref(null);

    // Зум выделением активен по умолчанию: после каждого рендера включаем
    // режим dataZoomSelect (кнопка toolbox может его выключить — вернём при
    // следующем обновлении опции).
    const activateZoomSelect = () => {
      const chart = chartRef.value && chartRef.value.chart;
      if (chart) {
        chart.dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: true,
        });
      }
    };

    watch([selectedFields, chartMode], () => {
      try {
        window.localStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(selectedFields.value));
        window.localStorage.setItem(MODE_STORAGE_KEY, chartMode.value);
      } catch (e) {
        /* localStorage может быть недоступен — молча игнорируем */
      }
    }, { deep: true });

    const isSelected = (metric) => selectedFields.value.includes(metric.field);

    const toggleMetric = (metric, event) => {
      if (event && event.shiftKey) {
        selectedFields.value = [metric.field];
        return;
      }
      const fields = selectedFields.value;
      if (fields.includes(metric.field)) {
        if (fields.length === 1) return;
        selectedFields.value = fields.filter((f) => f !== metric.field);
        return;
      }
      selectedFields.value = [...fields, metric.field];
    };

    const metricByField = (field) => METRICS.find((m) => m.field === field);
    const metricGroups = METRIC_GROUPS.map((g) => ({
      label: g.label,
      metrics: g.fields.map(metricByField).filter(Boolean),
    }));
    const selectedMetrics = computed(() =>
      selectedFields.value.map(metricByField).filter(Boolean)
    );
    const multiHint = computed(() => MULTI_HINTS[chartMode.value] || MULTI_HINTS.grouped);

    const chartOption = computed(() => {
      const metrics = selectedMetrics.value;
      if (!metrics.length) return {};
      const mode = chartMode.value;
      const formatters = metrics.map((m) => m.formatter);
      const allCounters = metrics.every((m) => m.counter);
      // Базовые значения метрик по корзинам
      const rawValues = metrics.map((metric) =>
        props.rows.map((row) => {
          const v = Number(row[metric.field]);
          return Number.isFinite(v) ? v : 0;
        })
      );
      // Стек %: доля каждой метрики в сумме выбранных метрик корзины
      if (mode === 'percent') {
        const totals = props.rows.map((_, idx) =>
          rawValues.reduce((acc, values) => acc + values[idx], 0)
        );
        rawValues.forEach((values) => {
          values.forEach((v, idx) => {
            values[idx] = totals[idx] ? (v / totals[idx]) * 100 : 0;
          });
        });
      }
      const seriesData = (metricIdx) => {
        const values = rawValues[metricIdx];
        return mode === 'cumulative' ? cumulativeSeries(values) : values;
      };
      const series = metrics.map((metric, metricIdx) => {
        if (mode === 'line' || mode === 'cumulative') {
          return {
            name: metric.label,
            type: 'line',
            smooth: mode === 'line',
            data: seriesData(metricIdx),
            itemStyle: { color: metric.color },
            lineStyle: { color: metric.color, width: 2 },
            symbol: 'circle',
            symbolSize: 5,
          };
        }
        return {
          name: metric.label,
          type: 'bar',
          stack: mode === 'stacked' || mode === 'percent' ? 'total' : undefined,
          data: seriesData(metricIdx),
          itemStyle: { color: metric.color },
          barMaxWidth: 28,
        };
      });
      const pctFormat = (v) =>
        `${(Number(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
      return {
        grid: { left: 8, right: 16, top: metrics.length > 1 ? 44 : 24, bottom: 50, containLabel: true },
        // Горизонтальный зум: колесо внутри графика + минимапа-слайдер снизу
        dataZoom: [
          { type: 'inside', filterMode: 'none' },
          { type: 'slider', height: 18, bottom: 8, filterMode: 'none' },
        ],
        // Зум выделением мышкой: кнопки в углу графика (рамка = выделить диапазон,
        // стрелка = шаг назад, круг = сброс)
        toolbox: {
          right: 0,
          top: 0,
          itemSize: 13,
          itemGap: 8,
          feature: {
            dataZoom: {
              yAxisIndex: 'none',
              title: { zoom: 'Зум выделением', back: 'Шаг назад' },
            },
            restore: { title: 'Сбросить зум' },
          },
        },
        legend: metrics.length > 1 ? { top: 0, left: 0, type: 'scroll', textStyle: { fontSize: 11 } } : undefined,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          confine: true,
          formatter: (params) => {
            const list = Array.isArray(params) ? params : [params];
            const lines = list.map((p) => {
              const valueText = mode === 'percent'
                ? pctFormat(p.value)
                : formatters[p.seriesIndex](p.value);
              return `${p.marker} ${p.seriesName}: <b>${valueText}</b>`;
            });
            return `${list[0].name}<br/>${lines.join('<br/>')}`;
          },
        },
        xAxis: {
          type: 'category',
          data: props.rows.map((row) => row.bin_label),
          axisLabel: { rotate: 45, fontSize: 10 },
        },
        yAxis: {
          type: 'value',
          max: mode === 'percent' ? 100 : undefined,
          minInterval: allCounters && mode !== 'percent' ? 1 : 0,
          axisLabel: {
            formatter: mode === 'percent' ? '{value}%' : (v) => formatters[0](v),
          },
        },
        series,
      };
    });
    return {
      CHART_MODES,
      MODE_HINTS,
      metricGroups,
      selectedFields,
      chartMode,
      chartRef,
      activateZoomSelect,
      isSelected,
      toggleMetric,
      multiHint,
      chartOption,
      fmtMoney,
      fmtNum,
      fmtPct,
    };
  },
};
</script>

<style scoped>
.temporal-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.chart-layout {
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (max-width: 1100px) {
  .chart-layout {
    grid-template-columns: 1fr;
  }
}

.chart-settings {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-label {
  font-size: 12px;
  font-weight: 500;
  color: #667085;
}

.metrics-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.metric-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.metric-chip:hover {
  border-color: #98a2b3;
}

.metric-chip.selected {
  border-color: #007bff;
  background: #eff6ff;
}

.metric-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.settings-hint {
  font-size: 11px;
  color: #98a2b3;
}

.chart-block {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  display: flex;
  flex-direction: column;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
}

/* График растягивается на всю высоту блока (ряд грида = высота сайдбара) */
.chart {
  width: 100%;
  flex: 1;
  min-height: 320px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 160px;
  color: #98a2b3;
  font-size: 14px;
}

.table-scroll {
  overflow-x: auto;
  min-height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #344054;
  white-space: nowrap;
}

.stat-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  color: #475467;
  font-weight: 600;
  text-align: left;
}

.stat-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f2f4f7;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.id-cell {
  font-weight: 600;
}

.cell-warn {
  color: #d92d20;
  font-weight: 600;
}

.empty-table {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
}
</style>
